import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import type { MaskitoOptions } from '@maskito/core';
import { DataObject } from '../models/data-objects.models';

export interface CurrencyOptions {
  min?: number;
  max?: number;
  precision?: number;
  decimalSeparator?: string;
  decimalPseudoSeparators?: string[];
  decimalZeroPadding?: boolean;
  thousandSeparator?: string;
  prefix?: string;
  postfix?: string;
  minusSign?: string;
}

export const globalPercentOptions: DataObject = {
  precision: 2,
  decimalSeparator: '.',
  max: 100,
  min: -100,
  minusSign: '-',
};

export const ivPercentMask = (percentOptions?: DataObject): MaskitoOptions => {
  if (!percentOptions) {
    percentOptions = globalPercentOptions;
  }
  return maskitoNumberOptionsGenerator(percentOptions);
};

export const printZeros = (decimalLimit = 0) => {
  let zeros = '';
  for (let i = 0; i < decimalLimit; i++) {
    zeros = zeros + '0';
  }
  return zeros;
};

export const indianCurrencyNumber = (amount: string) => {
  return amount
    .replace(/\D/g, '')
    .replace(/(\d+?)(?=(\d\d)+(\d)(?!\d))(\.\d+)?/g, '$1,');
};

const transformIndianFormat = (
  value: string,
  selection: any, // [1,1]
  currencyOptions: any
) => {
  const originalLength = value.length;
  let caretPosition = selection[0];
  const decimalPosition = value.indexOf(currencyOptions.decimalSeparator);

  if (decimalPosition >= 0) {
    let leftSide = indianCurrencyNumber(value.substring(0, decimalPosition));
    let rightSide = indianCurrencyNumber(value.substring(decimalPosition));

    rightSide = rightSide.substring(0, currencyOptions.precision);

    value = `${leftSide}${currencyOptions.decimalSeparator}${rightSide}`;
  } else {
    value = indianCurrencyNumber(value);
  }

  const updatedLength = value.length;
  caretPosition = updatedLength - originalLength + caretPosition;

  const elementStateValue = value;
  const elementStateSelection = [caretPosition, caretPosition];

  return { elementStateValue, elementStateSelection: elementStateSelection[0] };
};

export const maskitoIndianCurrencyOptions = (indianCurrencyOptions: DataObject): MaskitoOptions => {
  return {
    mask: /^(?!,$)[\d,.]+$/, // Allows numbers, commas (not starting comma) and dots.
    preprocessors: [
      // Removes characters when back-spacing
      ({ elementState, data }, actionType) => {
        let { value, selection } = elementState;

        if (actionType === 'deleteBackward') {
          const [selectionFrom, selectionTo] = selection;
          const originalLength = value.length;

          if (value === '') {
            return {
              elementState: {
                selection,
                value,
              },
              data: data,
            };
          }

          const selectionDiff = selectionTo - selectionFrom;
          if (selectionDiff === 1) {
            const leftValue = value.substring(0, selectionFrom);
            const rightValue = value.substring(selectionTo);
            value = leftValue + rightValue;
          }

          if (selectionFrom === 0 && selectionTo === originalLength) {
            value = '';
          }
        }

        return {
          elementState: {
            selection,
            value,
          },
          data: data,
        };
      },
      // Applies the Indian Currency Format
      ({ elementState, data }) => {
        let { value, selection } = elementState;
        let { elementStateValue, elementStateSelection } = transformIndianFormat(value, selection, indianCurrencyOptions);
        
        return {
          elementState: {
            selection: [elementStateSelection, elementStateSelection],
            value: elementStateValue,
          },
          data: data,
        };
      },
    ],
    postprocessors: [
      // INFO: We can customize the prefix based on the userLocale info => maskitoPrefixPostprocessorGenerator('$'),
      ({ value, selection }, initialElementState) => {
        const [from, to] = selection;
        const noRepeatedLeadingZeroesValue = value.replace(/^0+/, '0');
        const removedCharacters = value.length - noRepeatedLeadingZeroesValue.length;
        return {
          value: noRepeatedLeadingZeroesValue, // User types "000000" => 0|
          selection: [
            Math.max(from - removedCharacters, 0),
            Math.max(to - removedCharacters, 0),
          ],
        };
      },
    ],
    plugins: [],
  };
};
